import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import ProjectNode from 'components/main/tree/ProjectNode';
import BaseTree from 'components/main/tree/BaseTree';

import {
  getAbsoluteRootTreeNode,
  getNewFolderType,
  getParents,
  isNullOrUndefined,
  showAlertSnackBar,
} from 'utils/Global';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { renameNodeDTO, TreeControlProps } from 'business/models/CustomModels';
import { TreeService } from 'business/services/TreeService';
import { TreeNodeDTO } from 'business/models/Models';
import { EPlateState } from 'enums/EPlatestate';
import useBroadcast, {
  CHANNEL_ACTIONS,
  CHANNEL_TYPES,
  MessageType,
} from 'business/broadcast';
import './style.scss';
import { containerNodeTypes } from './constants';
import { useTreeKeyboardHandler } from 'keyboardShortcuts/explorerHooks';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useHistory } from 'react-router-dom';
import { EagerLoadTreeDataSupplier } from 'business/tree/TreeDataSupplier';
export interface TreeControlState {
  isLoading: boolean;
  treenodes: any[];
  viewNodes: any;
  selectedNodeContainer: any;
  selectedNodes: any[]; // these nodes appear in center panel
  selectedExplorerNode: any; // these nodes appear in left tree
  breadcrumbs: any[];
  checkedExplorerNodes: number[];
  isContextBackground?: boolean;
}

export const TreeControlDefaultState: TreeControlState = {
  isLoading: true,
  treenodes: [],
  viewNodes: {},
  selectedNodeContainer: null,
  selectedNodes: [],
  selectedExplorerNode: null,
  breadcrumbs: [],
  checkedExplorerNodes: [],
  isContextBackground: false,
};

const mainHeight = '100%';

const getTreeNodeById = (nodes, pTreeNodeId) => {
  let treeNode;
  for (let i = 0; i < nodes.length; i++) {
    let tempTreeNode = {};
    tempTreeNode = nodes[i];
    if (nodes[i].id === pTreeNodeId) {
      treeNode = tempTreeNode;
      break;
    } else if (nodes[i].children) {
      const _treeNode = getTreeNodeById(nodes[i].children, pTreeNodeId);

      if (_treeNode) {
        treeNode = _treeNode;
      }
    }
  }
  return treeNode;
};

const getChildren = (nodes, pTreeNodeId) => {
  let result;
  nodes.some(
    (node) =>
      (result =
        node.id === pTreeNodeId
          ? node.children
          : getChildren(node.children || [], pTreeNodeId)),
  );
  return result;
};

let crumbs = [];
const TreeControl = forwardRef((props: TreeControlProps, ref) => {
  const {
    setTreeControlState,
    treeControlState,
    shouldRenderNode,
    showQuickAccessNode = true,
    dataSupplier,
    setParentNode,
    commonTreeNodes,
    setCommonTreeNodes,
    isShowContext,
    onNodeSelect,
    onCheckboxChange,
    backgroundColor = '#D9D9D9',
    allowedRootTreeNodeTypes,
    shouldListenToBroadcastedEvents = true,
    showRecycleBinNode = true,
    treeNodeIdToLoadTree,
    defaultFirstSelectedNode = ETreeNodeTypes.Projects,
    onNodeClickSelectPostAction,
    selectAllNodes,
    externalDragHandling = false,
    nodeDoubleClickHandler = (n) => {},
    isSearchEnabled = false,
  } = props;
  const [nodes, setNodes] = useState([]);
  const [loadedTreeNodes, setLoadedTreeNodes] = useState([]);
  const [expandedTreeNodeIds, setExpandedTreeNodesIds] = useState([]);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const [scrollToId, setScrolltoId] = useState<number>(null);
  const history = useHistory();
  const latestSelectedIdRef = useRef<number | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      filterQuickAccess,
      onRefreshTreeNode,
      onRefreshTreeNodeAfterRename,
      onRefreshTreeNodeAfterForceUnlock,
      onRefreshTreeNodeAfterPlateStateChange,
      onRefreshTreeNodeAfterPlateBarcodeChange,
      OnDoubleClick,
      newNode,
      onClickNodeChildren,
      onRefreshTreeNodeAfterProteinVariantChange,
      generateAllTreeNodesById,
    }),
    [treeControlState, treeControlState.viewNodes, newNode, expandedTreeNodeIds],
  );
  const treeContainerRef = useRef(null);

  const generateAllTreeNodesById = useCallback(
    async (treeNodeId, isQuickAccessNode = false, signal?: AbortSignal) => {
      try {
        let selectedNode = {};
        const expandedTreeNodes = [];

        treeControlState.isLoading = true;
        setTreeControlState({
          ...treeControlState,
        });

        const treeService: TreeService = new TreeService();
        let nodes = [];
        let parentId = treeNodeId;
        let childNodes;
        try {
          childNodes = await treeService.getTreeNodesService(treeNodeId, null, signal);
          nodes = childNodes;
        } catch (error) {
          if (error.status === 404) {
            window.alert(
              'File not found. It may have been deleted or you dont have access.',
            );
            window.location.href = '/project';
            return;
          } else if (error.status === 403) {
            showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, error.message);
            history.push('/');
            return;
          } else {
            throw error;
          }
        }
        while (parentId != -1) {
          const tempParentId = await treeService.getParentIdByTreeNodeId(parentId);
          childNodes = await treeService.getTreeNodesService(tempParentId, null, signal);
          const parentNodeIndex = childNodes.findIndex((cn) => cn.id == parentId);

          if (parentNodeIndex !== -1) {
            childNodes[parentNodeIndex].children = nodes;
            childNodes[parentNodeIndex].state.expanded = true;
            expandedTreeNodes.push(childNodes[parentNodeIndex].id);
            nodes = childNodes;
          }
          if (parentId === treeNodeId) {
            selectedNode = childNodes[parentNodeIndex];
          }

          parentId = tempParentId;
        }

        if (showQuickAccessNode) {
          if (isQuickAccessNode) {
            /**
             * since we already know that quickaccess order is first then we don't need to find the object.
             * we can just use first index of array and it will be quickaccess.
             */
            nodes[0] = { ...treeControlState.treenodes[0], state: { expanded: true } };
            expandedTreeNodes.push(-2);
          }
          const quickAccessNodes = await treeService.getTreeNodesService(
            -2,
            null,
            signal,
          );
          if (!nodes.length) {
            treeControlState.isLoading = false;
            setTreeControlState({
              ...treeControlState,
            });
            return;
          }
          nodes[0].children = quickAccessNodes;
        }
        if (!showRecycleBinNode) {
          nodes = nodes.filter((_node) => _node.type !== ETreeNodeTypes.RecycleBin);
        }
        if (!selectedNode) {
          window.location.href = '/';
          return;
        }
        treeControlState.treenodes = nodes;
        treeControlState.isLoading = false;
        treeControlState.selectedExplorerNode = selectedNode;

        setTreeControlState(cloneDeep(treeControlState));
        setExpandedTreeNodesIds(expandedTreeNodes);
        setLoadedTreeNodes([]);

        await onClickNodeChildren(selectedNode);
      } catch (e) {
        if (signal?.aborted) {
          return;
        }
        throw e;
      }
    },
    [treeControlState, treeControlState.treenodes, treeControlState.checkedExplorerNodes],
  );

  async function fetchData(signal?: AbortSignal) {
    crumbs = [];
    let data = [];
    try {
      if (dataSupplier) {
        treeControlState.isLoading = true;
        const supplier = new dataSupplier();
        data = await supplier.getTreeNodes(null, allowedRootTreeNodeTypes, signal);
        if (data) {
          treeControlState.isLoading = false;
        }

        const treeService: TreeService = new TreeService();
        if (showQuickAccessNode && data?.length) {
          const quickAccessNodes = await treeService.getTreeNodesService(
            -2,
            null,
            signal,
          );
          data[0].children = quickAccessNodes;
        }

        if (!showRecycleBinNode) {
          data = data.filter((_node) => _node.type !== ETreeNodeTypes.RecycleBin);
        }
        treeControlState.treenodes = data;
        setTreeControlState({ ...treeControlState });
        const projectNode = data?.find((item) => item.name === defaultFirstSelectedNode);
        if (projectNode) {
          onClickNodeChildren(projectNode);
          OnDoubleClick(projectNode);
        }
      }
    } catch {
      return;
    }
  }

  const handleBroadcastMessageRecieve = useCallback(
    async (messageEvent: MessageEvent<MessageType<TreeNodeDTO>>) => {
      const { action, payload: node } = messageEvent.data;
      if (!node.parentId && action !== CHANNEL_ACTIONS.EXPLORER_RENAME_NODE) {
        return;
      }

      switch (action) {
        case CHANNEL_ACTIONS.EXPLORER_ADD_NODE:
        case CHANNEL_ACTIONS.EXPLORER_DELETE_NODE:
          const latestSelectedId = latestSelectedIdRef.current;
          if (
            latestSelectedId &&
            latestSelectedId !== treeControlState.selectedExplorerNode?.id
          ) {
            return;
          }
          let parentNode = getTreeNodeById(treeControlState.treenodes, node.parentId);
          if (!parentNode) {
            const projectsNodeId = await getAbsoluteRootTreeNode(node.parentId);
            parentNode = getTreeNodeById(treeControlState.treenodes, projectsNodeId);
          }
          onRefreshTreeNode(
            parentNode,
            treeControlState.selectedExplorerNode.id === node.parentId,
          );
          break;
        case CHANNEL_ACTIONS.EXPLORER_RENAME_NODE:
          onRefreshTreeNodeAfterRename({ id: node.id, newName: node.name });
          break;

        default:
          return;
      }
    },
    [treeControlState],
  );

  useEffect(() => {
    if (explorerBroadcastChannel && shouldListenToBroadcastedEvents) {
      explorerBroadcastChannel.onmessage = handleBroadcastMessageRecieve;
    }
  }, [explorerBroadcastChannel, handleBroadcastMessageRecieve]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    if (
      dataSupplier === EagerLoadTreeDataSupplier &&
      treeControlState.treenodes.length > 0
    ) {
      return;
    }
    if (treeNodeIdToLoadTree && treeNodeIdToLoadTree !== -1) {
      generateAllTreeNodesById(treeNodeIdToLoadTree, null, signal);
    } else {
      fetchData(signal);
    }

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (treeControlState.selectedNodeContainer) {
      const parents = getParents(nodes, treeControlState.selectedNodeContainer.id);
      if (isNullOrUndefined(parents)) {
        return;
      }
      const parentsWithChildrens = parents.filter((p) => !isNullOrUndefined(p.children));
      const state = treeControlState;
      state.viewNodes = parentsWithChildrens[0].children;
      setTreeControlState({ ...state });
    }
  }, []);

  async function filterQuickAccess(treeNodeIds: Array<number>) {
    const state = { ...treeControlState };
    const filteredQaNodes = state.treenodes[0]?.children.filter(
      (a) => !treeNodeIds.includes(a?.extra?.quickAccessMeta?.originalId),
    );
    if (filteredQaNodes.length) {
      state.treenodes[0].children = filteredQaNodes;
    }
    setTreeControlState(state);
  }

  async function onRefreshTreeNode(
    node,
    shouldUpdateViewNodes = true,
    newChildrenId = null,
    selectedNodesToSet = null, // it should be a []
    selectParentExplorerNode = false,
  ) {
    // TO DO: fix proper state management through redux later as current states are incosistent
    let treeState = null;
    let _selectedNodesToSet = selectedNodesToSet;

    if (!treeState) {
      treeState = treeControlState;
    }
    const nodes = await updateChildren(node, treeState.treenodes);
    const curNode = getTreeNodeById(nodes, node.id);

    if (shouldUpdateViewNodes) {
      treeState.viewNodes = curNode?.children;
    }
    treeState.treenodes = nodes;

    if (newChildrenId) {
      curNode.state.expanded = true;
      setExpandedTreeNodesIds([...expandedTreeNodeIds, node.id]);
      const newNode = getTreeNodeById(nodes, newChildrenId);
      const breadcrumbs = getParents(treeState.treenodes, newNode.id);
      treeState.selectedExplorerNode = newNode;
      treeState.selectedNodes = [newNode];
      treeState.viewNodes = [];
      treeState.breadcrumbs = breadcrumbs;
      setScrolltoId(newNode.id);
      if (setParentNode) {
        setParentNode(newNode);
      }
    }

    let explorerNodeToSelect = treeState.selectedExplorerNode;
    if (
      _selectedNodesToSet &&
      treeState.selectedExplorerNode?.id === treeState.selectedNodes[0]?.id
    ) {
      _selectedNodesToSet = [getTreeNodeById(nodes, node.parentId)];
    } else if (
      selectParentExplorerNode &&
      curNode.id === treeState.selectedExplorerNode?.parentId &&
      node.parentId
    ) {
      explorerNodeToSelect = getTreeNodeById(nodes, node.parentId);
      const breadcrumbs = getParents(treeState.treenodes, explorerNodeToSelect.id);
      treeState.breadcrumbs = breadcrumbs;
    }

    setTreeControlState({
      ...treeState,
      selectedExplorerNode: explorerNodeToSelect,
      selectedNodes: _selectedNodesToSet || treeState.selectedNodes,
    });
  }

  const onRefreshTreeNodeAfterRename = useCallback(
    (node: renameNodeDTO) => {
      const newNode = getTreeNodeById(treeControlState.treenodes, node.id);
      if (!newNode) {
        return;
      }
      newNode.name = node.newName;
      const breadCrumbNode = getTreeNodeById(treeControlState.breadcrumbs, node.id);
      if (breadCrumbNode) {
        breadCrumbNode.name = node.newName;
      }
      setTreeControlState({ ...treeControlState, selectedNodes: [{ ...newNode }] });
      /**
       * Broadcast change to other tabs/windows
       */
      explorerBroadcastChannel.postMessage({
        action: CHANNEL_ACTIONS.EXPLORER_RENAME_NODE,
        payload: newNode,
      });
    },
    [treeControlState.viewNodes],
  );

  const onRefreshTreeNodeAfterForceUnlock = async (node) => {
    const newNode = getTreeNodeById(treeControlState.treenodes, node.id);
    newNode.locked = false;
    setTreeControlState({ ...treeControlState, selectedNode: { ...newNode } });
  };
  const onRefreshTreeNodeAfterPlateStateChange = async (
    node,
    plateState: EPlateState,
    experimentState: string,
    plateIdsToDispense: number[],
  ) => {
    const newNode: TreeNodeDTO = getTreeNodeById(treeControlState.treenodes, node.id);
    if (newNode?.extra?.plateState >= 0) {
      newNode.extra.plateState = plateState;
    }
    if (!plateIdsToDispense?.length) {
      newNode?.extra?.plates?.map((plate) => (plate.plateState = plateState));
    } else {
      newNode?.extra?.plates?.map(
        (plate) =>
          (plate.plateState = plateIdsToDispense?.includes(plate.id)
            ? plateState
            : plate.plateState),
      );
    }
    if (newNode?.extra?.state) {
      newNode.extra.state = experimentState;
    }
    setTreeControlState({ ...treeControlState, selectedNodes: [{ ...newNode }] });
  };

  const onRefreshTreeNodeAfterPlateBarcodeChange = async (
    node,
    plateId: number,
    barcode: string,
  ) => {
    const newNode: TreeNodeDTO = getTreeNodeById(treeControlState.treenodes, node.id);
    if (newNode?.extra?.plates) {
      newNode.extra.plates = newNode.extra.plates?.map((plate) =>
        plate.id === plateId ? { ...plate, barcode } : plate,
      );
    }
    setTreeControlState({ ...treeControlState, selectedNodes: [{ ...newNode }] });
  };

  const onRefreshTreeNodeAfterProteinVariantChange = async (
    treeNodeId,
    isVariantExists,
  ) => {
    const newNode = getTreeNodeById(treeControlState.treenodes, treeNodeId);
    newNode.extra.isVariantExist = isVariantExists;
    setTreeControlState({ ...treeControlState, selectedNodes: [{ ...newNode }] });
  };

  async function updateChildren(node, treeNodesCopy) {
    const supplier = new dataSupplier();
    const data = await supplier.getTreeNodes(node.id);
    const nodes = generateArrayForNewChildrens(
      treeControlState.treenodes,
      data,
      node.id,
      expandedTreeNodeIds.find((id) => id === node.id) ? true : null,
      false,
    );
    treeNodesCopy = [...nodes];
    if (!isNullOrUndefined(data) && data.length > 0) {
      await Promise.all(
        data.map(async (tn) => {
          const isFound = loadedTreeNodes.find((id) => id === tn.id);
          if (isFound) {
            await updateChildren(tn, treeNodesCopy);
          }
        }),
      );
    }
    return treeNodesCopy;
  }

  function OnClickSelect(node) {
    if (onNodeClickSelectPostAction) {
      onNodeClickSelectPostAction(node);
    }
    const state = treeControlState;
    const nodeId = node.id;
    if (setParentNode) {
      setParentNode(node);
    }
    crumbs = getParents(treeControlState.treenodes, nodeId);
    if (!node.locked) {
      const cNodes = getChildren(treeControlState.treenodes, nodeId);
      if (cNodes?.length) {
        state.viewNodes = cNodes;
        state.selectedExplorerNode = node;
        state.breadcrumbs = crumbs;
      } else if (
        // selected node doesn't have any children and we should go one level up to select the nodes from its parents
        // Like experiment is selected then we will be displaying children of experiment's parent
        crumbs?.length >= 2 &&
        node.type !== ETreeNodeTypes.Project &&
        node.type !== ETreeNodeTypes.ProjectFolder &&
        node.type !== ETreeNodeTypes.Projects &&
        node.type !== ETreeNodeTypes.ProjectsFolder &&
        node.type !== ETreeNodeTypes.ScreensFolder &&
        node.type !== ETreeNodeTypes.SearchesFolder &&
        node.type !== ETreeNodeTypes.SynchrotronTripv4Folder
      ) {
        state.viewNodes = crumbs[1].children;
        state.selectedExplorerNode = node;
        state.breadcrumbs = crumbs;
      } else {
        state.viewNodes = [];
      }
    } else {
      state.viewNodes = [];
    }
    state.selectedNodes = [node];
    state.breadcrumbs = crumbs;
    state.selectedExplorerNode = node;
    state.isLoading = false;
    state.isContextBackground = false;
    setTreeControlState({ ...state });
  }

  function generateArrayForNewChildrens(
    nodes,
    newChildren,
    pTreeNodeId,
    expanded,
    isUpdateViewNodes = true,
  ) {
    interface Node {
      children: React.ReactNode;
      state: any;
    }
    const array = [];
    for (let i = 0; i < nodes.length; i++) {
      let node: Node = {
        children: '',
        state: '',
      };
      node = nodes[i];
      if (nodes[i].children) {
        node.children = generateArrayForNewChildrens(
          nodes[i].children,
          newChildren,
          pTreeNodeId,
          expanded,
          isUpdateViewNodes,
        );
      }
      if (nodes[i].id === pTreeNodeId) {
        node.children = newChildren;
        node.state = { expanded };

        if (isUpdateViewNodes) {
          const state = treeControlState;
          state.viewNodes = node.children;
        }
      }
      array.push(node);
    }
    return array;
  }

  async function OnDoubleClick(node, expandAndSelect = true, expandOnSelection = true) {
    if (!node?.id) {
      return;
    }
    if (node?.extra?.quickAccessMeta?.originalId) {
      generateAllTreeNodesById(node.extra.quickAccessMeta.originalId, true);
      return;
    }
    latestSelectedIdRef.current = node.id;
    // cancel previous pending request
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    const controller = new AbortController();
    activeRequestRef.current = controller;
    const signal = controller.signal;

    const state = treeControlState;
    let treeNodes = treeControlState.treenodes;
    const expanded = expandOnSelection ? node?.state?.expanded || false : true;
    const html = document.getElementsByTagName('html')[0];
    let data = [];
    html.style.cssText = '--cursor: wait';
    const { id } = node;
    if (id === undefined) {
      return;
    }
    if (expandOnSelection) {
      if (expandedTreeNodeIds.find((tnIds) => tnIds === node.id)) {
        setExpandedTreeNodesIds((prev) => prev.filter((x) => x !== id));
      } else {
        setExpandedTreeNodesIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }

    if (node.children && node.children.length > 0) {
      html.style.cssText = '--cursor: default';
      const nodes = generateArrayForNewChildrens(
        treeControlState.treenodes,
        node.children,
        id,
        !expanded,
        expandAndSelect,
      );

      if (expandAndSelect) {
        treeNodes = nodes;
        OnClickSelect(node);
      }
      return;
    }

    for (let iter = 0; iter < loadedTreeNodes.length; iter++) {
      if (loadedTreeNodes[iter] === id) {
        html.style.cssText = '--cursor: default';
        if (expandAndSelect) {
          treeNodes = loadedTreeNodes[iter];
          OnClickSelect(node);
        }
        return;
      }
    }

    if (dataSupplier) {
      const supplier = new dataSupplier();
      try {
        data = await supplier.getTreeNodes(id, [], signal);
      } catch (err) {
        if (signal.aborted) {
          return;
        }
        throw err;
      }
    }
    if (!signal.aborted) {
      loadedTreeNodes.push(id);
    }

    html.style.cssText = '--cursor: default';

    const onlyProjectsAndFolder = data.filter(
      (node) =>
        node.type == ETreeNodeTypes.Project ||
        node.type == ETreeNodeTypes.ProjectsFolder ||
        node.type == ETreeNodeTypes.ScreensFolder ||
        node.type == ETreeNodeTypes.ProjectFolder ||
        node.type == ETreeNodeTypes.QuickAccess,
    );
    let deepCopyNodes = [];
    if (!commonTreeNodes || commonTreeNodes?.length == 0) {
      deepCopyNodes = cloneDeep(treeNodes);
    } else {
      deepCopyNodes = cloneDeep(commonTreeNodes);
    }
    const filteredNewlyGenerateNodes = generateArrayForNewChildrens(
      deepCopyNodes,
      [...onlyProjectsAndFolder],
      id,
      !expanded,
      expandAndSelect,
    );
    const newlyGeneratedNodes = generateArrayForNewChildrens(
      treeNodes,
      data,
      id,
      !expanded,
      expandAndSelect,
    );
    setNodes(newlyGeneratedNodes);
    if (setCommonTreeNodes) {
      setCommonTreeNodes(filteredNewlyGenerateNodes);
    }
    if (expandAndSelect) {
      treeNodes = newlyGeneratedNodes;
      crumbs = getParents(treeNodes, id);
      if (expandOnSelection) {
        setExpandedTreeNodesIds((prev) => [
          ...new Set([...prev, ...(crumbs?.map((item) => item.id) ?? [])]),
        ]);
      }
      if (latestSelectedIdRef.current !== node.id) {
        return;
      }
      OnClickSelect(node);
      state.breadcrumbs = crumbs;
      state.selectedExplorerNode = node;
    }
    setTreeControlState({ ...state });
  }
  function onClickNodeChildren(selNode) {
    if (selNode?.extra?.quickAccessMeta?.originalId) {
      return;
    }
    OnClickSelect(selNode);
  }

  async function newNode(newChildren: TreeNodeDTO, children, pTreeNodeId) {
    const node: TreeNodeDTO = getTreeNodeById(treeControlState.treenodes, pTreeNodeId);
    if (containerNodeTypes.includes(newChildren.type as ETreeNodeTypes)) {
      node.hasContainerNode = true;
      onRefreshTreeNode(node, true, newChildren.id);
    } else {
      onRefreshTreeNode(node);
    }
    return null;
  }

  const generateNode = useCallback(
    (node, rest) => {
      return (
        <ProjectNode
          data={node}
          selectedNode={treeControlState.selectedExplorerNode}
          rest={rest}
          newFolderType={getNewFolderType(treeControlState.treenodes, node)}
          newNode={newNode}
          onClickHandler={OnClickSelect}
          openDocument={(expandAndSelect = true) => {
            OnDoubleClick(
              getTreeNodeById(treeControlState.treenodes, node.id),
              expandAndSelect,
            );
            if (onNodeSelect && expandAndSelect) {
              onNodeSelect();
            }
          }}
          onContextMenuClick={() => {}}
          onClickNodeChildren={onClickNodeChildren}
          onRefreshTreeNode={onRefreshTreeNode}
          isShowContext={isShowContext}
          onCheckboxChange={onCheckboxChange}
          treeControlState={treeControlState}
          backgroundColor={backgroundColor}
          externalDragHandling={externalDragHandling}
          onDoubleClick={nodeDoubleClickHandler}
        />
      );
    },
    [
      treeControlState.selectedExplorerNode,
      treeControlState.treenodes,
      isShowContext,
      treeControlState.viewNodes,
      expandedTreeNodeIds,
      TreeControlDefaultState.checkedExplorerNodes,
      treeControlState.selectedNodes,
      onCheckboxChange,
      onNodeSelect,
      nodeDoubleClickHandler,
    ],
  );

  const handleChange = useCallback(
    (nodes) => setTreeControlState((prvState) => ({ ...prvState, treenodes: nodes })),
    [],
  );

  useEffect(() => {
    setTreeControlState(treeControlState);
  }, []);

  useTreeKeyboardHandler({
    treeContainerRef: treeContainerRef,
    selectedExplorerNode: treeControlState?.selectedExplorerNode,
    treenodes: treeControlState?.treenodes,
    onSelectNode: OnClickSelect,
    onToggleExpand: OnDoubleClick,
    getTreeNodeById: getTreeNodeById,
    onCtrlAPress: selectAllNodes,
  });

  return (
    <BaseTree
      containerRef={treeContainerRef}
      mainHeight={mainHeight}
      handleChange={handleChange}
      treenodes={treeControlState.treenodes}
      generateNode={generateNode}
      shouldRenderNode={shouldRenderNode}
      expandedTreeNodeIds={expandedTreeNodeIds}
      treeControlState={treeControlState}
      backgroundColor={backgroundColor}
      scrollToId={scrollToId}
      isLoading={treeControlState.isLoading}
      isSearchEnabled={isSearchEnabled}
    />
  );
});
export default TreeControl;
